import { apiClient } from "@/api/client";
import type { PricingRule, Zone } from "@/types/domain";

/**
 * Talks to apps/pricing-service directly (gateway routes /pricing(/|$) to it)
 * rather than through the /admin/fares delegate, because the pricing service
 * exposes the full rule list and full zone CRUD (including delete), which the
 * admin-service delegate does not.
 */
export const pricingApi = {
  /** GET /pricing/rules — every configured rule ('default' + one per zone). */
  listRules: () => apiClient.get<PricingRule[]>("/pricing/rules").then((r) => r.data),

  /** PATCH /pricing/rules/:key — ADMIN/OPERATIONS only. */
  updateRule: (key: string, payload: Partial<PricingRule>) =>
    apiClient.patch<PricingRule>(`/pricing/rules/${key}`, payload).then((r) => r.data),

  /** POST /pricing/rules/:key — ADMIN/OPERATIONS only. 409s if the key already exists. */
  createRule: (key: string, payload: Partial<PricingRule>) =>
    apiClient.post<PricingRule>(`/pricing/rules/${key}`, payload).then((r) => r.data),
};

export const zonesApi = {
  /** GET /pricing/zones/all — includes inactive zones (admin view). */
  list: () => apiClient.get<Zone[]>("/pricing/zones/all").then((r) => r.data),

  /** POST /pricing/zones — `ring` is [lng,lat] points, min 3, auto-closed by the backend. */
  create: (payload: {
    name: string;
    city?: string;
    ring: number[][];
    active?: boolean;
    pricingRuleKey?: string;
  }) => apiClient.post<Zone>("/pricing/zones", payload).then((r) => r.data),

  update: (id: string, payload: Partial<Zone>) =>
    apiClient.patch<Zone>(`/pricing/zones/${id}`, payload).then((r) => r.data),

  setActive: (id: string, active: boolean) =>
    apiClient.patch<Zone>(`/pricing/zones/${id}`, { active }).then((r) => r.data),

  remove: (id: string) => apiClient.delete(`/pricing/zones/${id}`).then((r) => r.data),
};
